'use client';

import clsx from 'clsx';
import { fromJS } from 'immutable';
import { usePathname, useRouter } from 'next/navigation';
import { useState } from 'react';
import { AiOutlineFundView, AiOutlineLineChart } from 'react-icons/ai';
import { BiExport } from 'react-icons/bi';
import { CgLock } from 'react-icons/cg';
import { FaRegSave } from 'react-icons/fa';
import { FiDatabase } from 'react-icons/fi';
import { ImEmbed2 } from 'react-icons/im';
import { IoAnalyticsSharp } from 'react-icons/io5';
import { MdPublic } from 'react-icons/md';
import { PiFileCsv } from 'react-icons/pi';
import { RiTeamLine } from 'react-icons/ri';
import { SiOpenai } from 'react-icons/si';
import { SlSupport } from 'react-icons/sl';
import { TiChartLine } from 'react-icons/ti';

import authenticateThenRedirect from '@/lib/authenticateThenRedirect';
import { planTiers } from '@/lib/config';
import { trackClick } from '@/lib/event_tracker';

import * as Session from '@/lib/data/session';

import PricingTable from './PricingTable';

function BillingIntervalPickerButton({
  className,
  session,
  pathname,
  billingInterval,
  currentBillingInterval,
  setCurrentBillingInterval,
  children,
}) {
  return (
    <button
      className={clsx(
        'border border-gray-200 bg-white px-4 py-4 font-medium text-gray-900 hover:bg-gray-50 focus:text-blue-700 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600 dark:hover:text-white dark:focus:text-white dark:focus:ring-blue-500 sm:text-xl lg:px-8',
        currentBillingInterval === billingInterval &&
          'border-2 border-sky-200 bg-sky-50 text-blue-700',
        className
      )}
      onClick={(_e) => {
        setCurrentBillingInterval(billingInterval);

        trackClick(session, pathname, 'Billing Interval Picker Button', {
          billingInterval,
        });
      }}
      type="button"
    >
      {children}
    </button>
  );
}

export default function Billing({ sessionData, className }) {
  const session = fromJS(sessionData);
  const [billingInterval, setBillingInterval] = useState('monthly');
  const router = useRouter();
  const pathname = usePathname();

  return (
    <div className={clsx([className, 'flex flex-col gap-8'])}>
      <div className="flex justify-center" role="group">
        <BillingIntervalPickerButton
          className="rounded-l-lg"
          session={session}
          pathname={pathname}
          billingInterval="monthly"
          currentBillingInterval={billingInterval}
          setCurrentBillingInterval={setBillingInterval}
          type="button"
        >
          Monthly billing
        </BillingIntervalPickerButton>

        <div className="relative">
          <span className="bg-background fill-background absolute left-1/2 top-0 inline-flex w-max -translate-x-1/2 -translate-y-2 items-center rounded-full border bg-red-400 px-2 py-0.5 text-xs font-medium text-white">
            2 months free!
          </span>

          <BillingIntervalPickerButton
            className="rounded-r-lg"
            session={session}
            pathname={pathname}
            billingInterval="yearly"
            currentBillingInterval={billingInterval}
            setCurrentBillingInterval={setBillingInterval}
            type="button"
          >
            Yearly billing
          </BillingIntervalPickerButton>
        </div>
      </div>

      <div className="text-center [text-wrap:balance]">
        <span className="font-bold">50% off</span> while in early access. Prices
        will raise!
      </div>

      <div className="grid grid-cols-1 gap-y-8 lg:grid-cols-3 lg:gap-x-8 lg:gap-y-0">
        <PricingTable
          session={session}
          billingInterval={billingInterval}
          planName="Hobby"
          planSubscriptionTier={planTiers.tier0Plans}
          planButtonText="Get started"
          planFeatures={[
            {
              Icon: AiOutlineLineChart,
              text: 'Create and share annotated charts',
            },
            {
              Icon: ImEmbed2,
              text: 'Embed annotated charts',
            },
            {
              Icon: BiExport,
              text: 'Export annotated charts as images',
            },
            {
              Icon: AiOutlineFundView,
              text: 'Your own data portfolio',
            },
            {
              Icon: PiFileCsv,
              text: 'Access to all community datasets',
            },
            {
              Icon: SiOpenai,
              text: 'Find insights in your data with GPT-3.5',
            },
            {
              Icon: MdPublic,
              text: 'All your datasets are public',
            },
          ]}
        />

        <PricingTable
          session={session}
          billingInterval={billingInterval}
          planName="Professional"
          planSubscriptionTier={planTiers.tier1Plans}
          planButtonText="Get started"
          planFeatures={[
            {
              Icon: AiOutlineLineChart,
              text: 'Create and share annotated charts',
            },
            {
              Icon: ImEmbed2,
              text: 'Embed annotated charts',
            },
            {
              Icon: BiExport,
              text: 'Export annotated charts as images',
            },
            {
              Icon: AiOutlineFundView,
              text: 'Your own data portfolio',
            },
            {
              Icon: PiFileCsv,
              text: 'Access to all community datasets',
            },
            {
              Icon: SiOpenai,
              text: (
                <span>
                  Find insights in your data with <b>GPT-4</b>
                </span>
              ),
            },
            {
              Icon: CgLock,
              text: (
                <span>
                  <b>Private</b> datasets
                </span>
              ),
            },
            {
              Icon: IoAnalyticsSharp,
              text: (
                <span>
                  <b>Analytics</b>
                </span>
              ),
            },
            {
              Icon: FaRegSave,
              text: (
                <span>
                  <b>Snapshot</b> websites and tweets used in annotations
                </span>
              ),
            },
          ]}
        />

        <PricingTable
          session={session}
          planName="Teams"
          planCostMessage="Starts at $490"
          planButtonText="Book a demo"
          planButtonAsLink="mailto:murilo@contextualize.ai"
          planFeatures={[
            {
              Icon: RiTeamLine,
              text: 'Multiple user accounts',
            },
            {
              Icon: FiDatabase,
              text: 'LDAP',
            },
            {
              Icon: TiChartLine,
              text: 'Branded annotated charts',
            },
            {
              Icon: SlSupport,
              text: 'Direct support from the founder',
            },
          ]}
        />
      </div>

      {(() => {
        if (Session.isAuthenticated(session)) {
          return null;
        }

        return (
          <div className="mt-8 justify-center text-center text-gray-400">
            Or create a{' '}
            <button
              className="text-gray-500"
              type="button"
              onClick={(_e) => {
                trackClick(session, pathname, 'Subscribe To Plan Button', {
                  planType: 'free',
                });

                authenticateThenRedirect(router, session, {
                  paidPlanSubscriberPath: pathname,
                  freePlanSubscriberPath: `${pathname}?free=true`,
                });
              }}
            >
              free account
            </button>{' '}
            to <span className="text-xl hover:text-red-400">♥</span> like the
            stories others are creating.
          </div>
        );
      })()}
    </div>
  );
}
